import { has, get } from 'lodash';

import * as api from 'api';
import getDateForUrl from 'api/getDateForUrl';

import CompetitionFilterModal from '../CompetitionFilter/CompetitionFilterModal';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { renderNotification } from '../ReactComponents/common/notifications/toasts';
import Select from '../Select/Select';
import Tools from '../Tools/Tools';
import WebInterfacesDialog, { WebInterfacesType } from '../WebInterfaces/WebInterfacesDialog';

const excludedTableColumns = ['sources'];

/**
 * Třída, která zajišťuje vykreslení tabulky a veškerou práci s ní.
 */
class Table {
	/**
	 * Pokud existuje element s class 'Page-section--table', tak vytvořím tabulku.
	 */
	constructor() {
		this.section = document.querySelector('.Page-section--table');

		if (this.section) {
			this.processedReportIds = window.SSP.data.unprocessedReportIds || [];

			this.setTableData();
			this.render();
			this.listenOnTableSize();
		}
	}

	/**
	 * Nastaví typ stránky (Weby || Detail webu).
	 * Získá data pro prvotní načtení z windows.SPP a z localStorage.
	 */
	setTableData() {
		this.pageName = this.section.getAttribute('data-source');
		this.data = Object.assign({}, window.SSP.data[this.pageName]);
		this.storageItemName = 'tableConfig' + this.pageName;
		this.setUrls();
		this.config = Tools.getFromLocalStorage(this.storageItemName, {
			pageFirstIndex: 0,
			pageLastIndex: 10,
			pageSize: 10,
		});

		// Tabulka může být vytvořena z několika stránek.
		switch (this.pageName) {
			case 'webs':
				this.pageIdName = 'webId';
				this.complexTable = true;

				break;
			case 'reports':
				this.pageIdName = '';
				this.complexTable = false;
				this.reportRefresher();

				break;
			default:
				this.pageIdName = '';
				this.complexTable = false;

				break;
		}
	}

	/**
	 * Počáteční vyrenderování tabulky. Pokud se nejedná o komplexní tabulku (weby/zony),
	 * tak se nerenderuje hlavička, patička, kde jsou filtry a další ovládací prvky.
	 */
	render() {
		this.initTableSize();

		if (this.complexTable) {
			this.renderHeader();
			this.listenOnDateChange();
		}

		this.renderTable();
		this.renderPagination();
		this.updatePaginationArrows();
	}

	/**
	 * Dokumentace API na ..dev.dszn.cz/api/docs/
	 */
	setUrls() {
		const urls = window.SSP.data.urls || {};
		this.url = {
			delete: urls.delete ? urls.delete : null,
			// ?webId=X&webId=Y...
			start: urls.start ? urls.start : null,
			// ?webId=X&webId=Y...
			stop: urls.stop ? urls.stop : null,
			// ?dateStart=Y-m-d&dateFinish=Y-m-d&interval=today&webId=X&webId=Y...
			tableData: urls.table + '?',
		};
	}

	/**
	 * Adds link <a> to report title.
	 * @param {Element} nameElem
	 * @param {string} link
	 * @param {string} name
	 */
	addLinkToReport(nameElem, link, name) {
		if (nameElem) {
			nameElem.innerText = '';

			nameElem.appendChild(
				Tools.createElement('a', {
					class: 'is-clickable',
					href: link,
					innerText: name,
				}),
			);
		}
	}

	/**
	 * Adds formats to report.
	 * @param {Element} formatsElem
	 * @param {Array} formats
	 */
	addFormatsToReport(formatsElem, formats) {
		if (formatsElem) {
			formats.forEach((format) => {
				const href = Tools.createElement('a', {
					href: format.urlDownload,
					innerText: window.SSP.translations.table.formats[format.type],
				});

				formatsElem.appendChild(href);
			});
		}
	}

	/**
	 * Updates report in table data and DOM.
	 * @param {Object} statusData
	 * @param {string} id
	 */
	updateReport(statusData, id) {
		const { status } = statusData.status;
		let oldStatus = '';

		// Obnovím status řádku v načtených datech.
		for (let i = 0; i < this.data.rows.length; i++) {
			if (this.data.rows[i].id === Number(id)) {
				oldStatus = this.data.rows[i].status.status;
				this.data.rows[i] = statusData;

				break;
			}
		}

		// Aktualizuji třídu a text statusu řádku v DOMu.
		const reportRow = this.table.querySelector(`tbody tr[data-id="${id}"]`);
		const statusElem = reportRow.querySelector('.Status');

		if (statusElem) {
			statusElem.classList.remove('Status--' + oldStatus);
			statusElem.classList.add('Status--' + status.replace(/\s/g, ''));
			statusElem.innerText = window.SSP.translations.table.statuses[status];
		}

		// Reporty v těchto stavech nejsou dokončené a je potřeba furt kontrolovat stav.
		if (~['processing', 'new', 'idle', 'working'].indexOf(status)) {
			this.processedReportIds.push(Number(id));
		} else if (status === 'finished' || status === 'done') {
			// Report, které nejsou finished se načítají bez formats, takže se musí nyní doplnit.
			const formatsElem = reportRow.querySelector('.Table-col--formatsForDownload');

			this.addFormatsToReport(formatsElem, statusData.formats);

			// Musí se doplnit i link na proklik do detailu.
			const nameElem = reportRow.querySelector('.Table-item-title');

			this.addLinkToReport(nameElem, statusData.name.urlDetails, statusData.name.name);
		}
	}

	/**
	 * Pokud byl nějaký report dokončen, tak aktualizuje status reportu v řádku tabulky.
	 * Pokud jsou všechny hotovy, tak ukončí kontrolu (interval).
	 * Stavy:
	 * processing - Zpracováváme
	 * new - Nový
	 * finished -> Hotovo
	 * @param {object} statuses {id: "new", id: "processing"}
	 * @param {object} interval
	 */
	checkReportStatus(statuses, interval) {
		// Uživatel může během provádění requestu kopírovat řádky a změnit processedReportIds.
		if (Object.keys(statuses).length !== this.processedReportIds.length) {
			return;
		} else if (!this.processedReportIds.length) {
			clearInterval(interval);

			return;
		}

		this.processedReportIds = [];

		for (const id in statuses) {
			this.updateReport(statuses[id], id);
		}

		if (!this.processedReportIds.length) {
			clearInterval(interval);
		}
	}

	/**
	 * Sestaví novou url pro kontrolu reportů.
	 * @return {string}
	 */
	getCheckReportUrl() {
		const ids = this.processedReportIds.map((id) => 'reportId=' + id).join('&');

		if (!ids.length) {
			return null;
		}

		return window.SSP.data.urls.check + '?' + ids;
	}

	/**
	 * Pokud na stránce jsou nezpracované reporty,
	 * tak se každé 3 vteřiny kontroluje zda už nejsou zpracovány,
	 * pokud ano, tak bude tabulka překreslena.
	 */
	reportRefresher() {
		if (this.processedReportIds.length) {
			const REFRESH_INTERVAL = 3000;

			const interval = setInterval(() => {
				const url = this.getCheckReportUrl();

				if (url) {
					api
						.GET(url, { loader: false })
						.then(({ data }) => {
							this.checkReportStatus(data.reportStatuses, interval);
						})
						.catch((error) => {
							console.error(error);
						});
				} else {
					clearInterval(interval);
				}
			}, REFRESH_INTERVAL);
		}
	}

	/**
	 * Překreslí hlavičku tabulky.
	 */
	redrawHeader() {
		this.table.removeChild(this.thead);
		this.renderTableHeader();
		this.table.appendChild(this.thead);
	}

	/**
	 * Překreslí tělo tabulky.
	 */
	redrawBody() {
		this.table.removeChild(this.tbody);
		this.renderTableBody();
		this.table.insertBefore(this.tbody, this.tfoot);
		// Zahazuji pole s otevřenými weby/zony.
		this.expandedRows = [];
	}

	/**
	 * Překreslí patičku tabulky.
	 */
	redrawFooter() {
		this.table.removeChild(this.tfoot);
		this.renderTableFooter();
		this.table.appendChild(this.tfoot);
	}

	/**
	 * Vrací string s vybranou aktivitou webů pro API cally.
	 * @param {string} selectedActivity active || all
	 * @param {string} prefix
	 * @return {string}
	 */
	getActivityForUrl(selectedActivity, prefix = '&') {
		return prefix + 'webActivity=' + selectedActivity;
	}

	/**
	 * Získá zaškrtnuté řádky
	 * @return {array of objects} Vrací pole objektů dat řádků. Přidá do nich i jejich index v tabulce.
	 */
	getCheckedRows() {
		const rows = [];

		this.data.rows.forEach((row, index) => {
			if (row.checked) {
				rows.push({ tableIndex: index, ...row });
			}
		});

		return rows;
	}

	/**
	 * Vytvoří ?webId=X&webId=Y... pro API call.
	 * @param {array} checkedRows Array of objects of the checked rows of table
	 * @param {string} prefix ? nebo & podle toho čím chceme začínat.
	 * @return {string} URL query string
	 */
	getIdsForUrl(checkedRows, prefix = '?') {
		return checkedRows.reduce((accumulator, row) => {
			accumulator += accumulator ? '&' : prefix;
			// eslint-disable-next-line typescript-eslint/restrict-template-expressions
			return `${accumulator}${this.pageIdName}=${row[this.pageIdName]}`;
		}, '');
	}

	/**
	 * Pokud uživatel změní datum pomocí kalendáře,
	 * tak budou načtena nová data. Přerenderuje se
	 * tělo a patička tabulky.
	 */
	listenOnDateChange() {
		window.addEventListener('calendar-changed', (e) => {
			let url =
				this.url.tableData +
				getDateForUrl(e.detail) +
				this.getActivityForUrl(this.selectActivityFilter.getSelectedOption());

			// Uživatel má zobrazen pouze výběr webů/zon.
			if (this.customSelection) {
				url += this.customSelection;
			}

			// Stáhne data pro nové časové období,
			// odstraní původní tbody/tfoot a vygeneruje nové.
			api
				.GET(url)
				.then(({ data }) => {
					this.data = data;

					if (this.config.sortedColumn) {
						this.sortTable(this.config.sortedColumn.columnType, this.config.sortedColumn.sorted);
					}

					this.initTableSize();
					this.redrawBody();
					this.redrawFooter();
					this.updatePaginationArrows();
				})
				.catch((error) => {
					console.error(error);
				});
		});
	}

	/**
	 * Při provedení hromadné akce -
	 * vybraným řádkům bude aktualizován stav a budou odškrtnuty.
	 * @param {string} state 'active' nebo 'inactive'
	 */
	redrawRowsStates(state) {
		this.data.rows.forEach((row) => {
			if (row.checked) {
				row.status.state = state;
				row.checked = false;
			}
		});

		this.redrawBody();
	}

	/**
	 * Stopnutí nebo spuštění všech vybraných webů/zón/...
	 * Včetně kontroly zda je nějaká zóna/web vybraná.
	 * @param {bool} toActivate true = start, false = stop
	 * @param {string} ids IDs in string for URL
	 */
	multipleStateChange(toActivate, ids) {
		if (ids) {
			const url = toActivate ? this.url.start : this.url.stop;

			api
				.POST(url + ids)
				.then(() => {
					this.redrawRowsStates(toActivate ? 'active' : 'inactive');
				})
				.catch((error) => {
					console.error(error);
				});
		}
	}

	/**
	 * handle click from the Common action option Activate or Deactivate
	 * @param {bool} toActivate true = start, false = stop
	 */
	selectCommonActionStateChange(toActivate) {
		const checkedRows = this.getCheckedRows();
		const idsQueryString = this.getIdsForUrl(checkedRows);

		// testuje, jestli existují nějaké neaktivní řádky k aktivaci nebo aktivní k deaktivaci
		const somethingToDo = checkedRows.reduce(
			(somethingToDo_, row) => ((row.status.state === 'active') === toActivate ? somethingToDo_ : true),
			false,
		);

		if (!somethingToDo) {
			return;
		}

		this.multipleStateChange(toActivate, idsQueryString);
	}

	/**
	 * Deletes multiple rows
	 * @param {string} ids IDs in string for URL
	 */
	multipleDelete(ids) {
		if (ids) {
			api
				.POST(this.url.delete + ids)
				.then(() => {
					const removeIndexes = [];

					this.getCheckedRows().forEach((row) => {
						// zařadí do seznamu k vymazání
						removeIndexes.push(row.tableIndex);
						// spustí animaci mazání
						// this.deleteRowAnimation(row); // nepředávám element, ale objekt z dat. Animovat nebudeme.
					});

					// vymazání z dat
					this.data.rows = this.data.rows.filter((row, index) => !removeIndexes.includes(index));

					// načasování překreslení tabulky
					// setTimeout(() => { // animovat nebudeme
					this.initTableSize();
					this.updatePaginationArrows();
					this.redrawBody();
					// }, 1000);
				})
				.catch((error) => {
					console.error(error);
				});
		}
	}

	selectCommonActionDelete() {
		const checkedRows = this.getCheckedRows();

		if (!checkedRows.length) {
			return;
		}

		const idsQueryString = this.getIdsForUrl(checkedRows);

		this.multipleDelete(idsQueryString);
	}

	/**
	 * Zobrazení všech vybraných webů/zón.
	 */
	selectCommonActionShowSelected() {
		const checkedRows = this.getCheckedRows();
		const idsQueryString = this.getIdsForUrl(checkedRows, '&');

		if (checkedRows.length) {
			this.customSelection = idsQueryString;
			this.onRowsSelectionUpdate();
			this.generateCancelSelectionButton();
		}
	}

	selectCommonActionFilterCompetitionSelected() {
		const selectedWebs = this.getCheckedRows().map(({ webId, name: { name } }) => ({
			id: webId,
			name,
		}));

		if (selectedWebs.length) {
			// eslint-disable-next-line no-new
			new CompetitionFilterModal(selectedWebs);
		}
	}

	/**
	 * Vygeneruje tlačítko, které umožňuje zrušení vybraných řádků.
	 */
	generateCancelSelectionButton() {
		const buttonClass = 'Table-cancelSelection';

		// If button already exists..
		if (this.section.querySelector(`.${buttonClass}`)) {
			return;
		}

		const cancelSelection = Tools.createElement('button', {
			class: buttonClass,
			innerText: window.SSP.translations.table.labels.cancelSelection,
		});

		cancelSelection.addEventListener('click', () => {
			this.customSelection = '';
			this.onRowsSelectionUpdate();
			Tools.removeElement(cancelSelection);
		});

		Tools.insertAfter(cancelSelection, this.selectCommonAction.getElement());
	}

	/**
	 * Obnoví tabulku a notifikuje graf o změne.
	 */
	onRowsSelectionUpdate() {
		const event = new CustomEvent('only-selected-rows', {
			detail: { selected: this.customSelection },
		});

		window.dispatchEvent(event);

		const url = this.url.tableData + getDateForUrl() + this.customSelection;

		api
			.GET(url)
			.then(({ data }) => {
				this.data = data;
				this.initTableSize();
				this.redrawHeader();
				this.redrawBody();
				this.redrawFooter();
				this.updatePaginationArrows();
			})
			.catch((error) => {
				console.error(error);
			});
	}

	/**
	 * Voláno pokud uživatel vybere hromadnou akci s weby.
	 * @param {string} selected
	 */
	onSelectCommmonAction(selected) {
		switch (selected) {
			case 'start':
				this.selectCommonActionStateChange(true);

				break;
			case 'stop':
				this.selectCommonActionStateChange(false);

				break;
			case 'showSelected':
				this.selectCommonActionShowSelected();

				break;
			case 'delete':
				this.selectCommonActionDelete();

				break;
			case 'filterCompetition':
				this.selectCommonActionFilterCompetitionSelected();

				break;
		}

		this.selectCommonAction.toggleSelect();
	}

	/**
	 * Fuknce pro updatování dat po změně filtru.
	 * Pro projevení změn musí mít zobrazen rozpad nějakého webu/zón.
	 */
	updateDataByFilterChange() {
		const rowExpandButtons = [];
		const promisesForExpand = [];

		this.expandedRows.forEach((rowId) => {
			rowExpandButtons.push(
				this.tbody.querySelector(
					// eslint-disable-next-line typescript-eslint/restrict-template-expressions
					`tr td span[data-${this.pageIdName}="${rowId}"]`,
				),
			);
		});

		rowExpandButtons.forEach((expandButton) => {
			this.collapseRow(expandButton.parentNode.parentNode, expandButton);
			promisesForExpand.push(this.expandRow(expandButton));
		});

		Promise.all(promisesForExpand)
			.then((values) => {
				values.forEach((data, index) => {
					if (data.rows.length) {
						rowExpandButtons[index].classList.add('is-open');

						data.rows.forEach((row) => {
							this.renderExpandRow(
								row,
								Number(rowExpandButtons[index].getAttribute('data-' + this.pageIdName)),
								rowExpandButtons[index].parentNode.parentNode,
							);
						});
					} else {
						rowExpandButtons[index].classList.remove('is-open');
						renderNotification({ feel: 'info', title: window.SSP.translations.messages.noData });
					}
				});
			})
			.catch((error) => {
				console.error(error);
			});
	}

	/**
	 * Ve vypisu webu se musi schovat v rozpadu řádku hodnoty
	 * zobrazeni a CPT pro rozpad podle reklamniho zdroje.
	 */
	isImpCPTHidden(type) {
		return (
			this.pageName === 'webs' &&
			this._selectedDimensionFilter === 'byDsp' &&
			(type === 'impressions' || type === 'cpt')
		);
	}

	/**
	 * Voláno pokud uživatel vybere filtrování statistik podle..
	 * @param {string} selected
	 */
	onSelectFilterByDimension(selected) {
		this._selectedDimensionFilter = selected;
		this.selectFilterByDimension.setSelectOption(selected);
		this.selectFilterByDimension.toggleSelect();

		if (this.expandedRows && this.expandedRows.length) {
			if (this.pageName === 'webs') {
				const expandedRowsBackup = [...this.expandedRows];

				this.redrawHeader();
				this.redrawBody();
				this.redrawFooter();
				this.expandedRows = [...expandedRowsBackup];
				this.updateDataByFilterChange();
			} else {
				this.updateDataByFilterChange();
			}
		} else {
			this.redrawHeader();
			this.redrawBody();
			this.redrawFooter();
		}

		// Notifikace grafu o změně.
		const event = new CustomEvent('data-dimension-changed', {
			detail: { selected },
		});

		window.dispatchEvent(event);
	}

	/**
	 * Fuknce, která sesortí tabulku.
	 * @param {Event} param Do proměnné param defaultně má přijít event. Dá se do ní ale poslat i {string} s názvem typu sloupce tabulky.
	 * @param {string} sorted Volitelný parametr. Přijímá 2 stringy: 'is-sortedUp' a 'is-sortedDown'. Pokud je tento parametr posl8n, tak se sloupec seřadí podle tohoto parametru.
	 * @example
	 * element.addEventListener('click', this.sortTable.bind(this));
	 * @example
	 * this.sortTable('opt');
	 * @example
	 * this.sortTable('opt', 'is-sortedUp');
	 * @example
	 *    if (this.config.sortedColumn) {
	 *        this.sortTable(this.config.sortedColumn.columnType, this.config.sortedColumn.sorted);
	 *    }
	 */
	sortTable(param, sorted = '') {
		let columnType = null;

		if (typeof param === 'string') {
			columnType = param;
		} else if (param.target.classList.contains('Table-th-cont')) {
			columnType = param.target.getAttribute('type');
		} else {
			return;
		}

		const column = this.thead.querySelector(`.Table-col--${columnType}`);

		if (!column) {
			return;
		}

		if (column.classList.contains('is-sortedUp')) {
			sorted = 'is-sortedUp';
		} else if (column.classList.contains('is-sortedDown')) {
			sorted = 'is-sortedDown';
		}

		if (sorted === '') {
			this.data.rows = this.sortColumn(this.data.rows, columnType);

			Array.from(document.querySelectorAll('.Table--primary thead tr th')).forEach((el) =>
				el.classList.remove('is-sortedUp', 'is-sortedDown'),
			);

			column.classList.add('is-sortedUp');
			sorted = 'is-sortedUp';
		} else if (typeof param === 'string') {
			this.data.rows = this.sortColumn(this.data.rows, columnType);

			if (sorted === 'is-sortedDown') {
				this.data.rows.reverse();
			}

			column.classList.add(sorted);

			return;
		} else {
			this.data.rows.reverse();
			sorted = Tools.replaceClass(column, 'is-sortedUp', 'is-sortedDown');
		}

		this.config.sortedColumn = {
			columnType,
			sorted,
		};

		Tools.updateLocalStorage(this.storageItemName, this.config);
		this.redrawBody();
	}

	/**
	 * Fuknce, která seřadí daný sloupec v poli.
	 * @param {array} data Data, ve ktrých jsou sloupce tabulky
	 * @param {string} column Definuje sloupec, podle kterého se budou data sortit
	 * @param {string} order Pořadí řazení.
	 * @return {array} Sesortěné pole
	 * @example
	 * this.data.rows = this.sortColumn(this.data.rows, columnType);
	 */
	sortColumn(data, column, order = 'sorted-up') {
		data.sort((a, b) => {
			if (a[column] === null) {
				return -1;
			} else if (b[column] === null) {
				return 1;
			} else {
				if (typeof a[column] === 'string') {
					return a[column].localeCompare(b[column]);
				} else if (typeof a[column] === 'object') {
					switch (column) {
						case 'created':
							return a[column].createdTimestamp - b[column].createdTimestamp;
						case 'createdAt':
							return a[column].createdAtTimestamp - b[column].createdAtTimestamp;
						case 'updatedAt':
							return a[column].updatedAtTimestamp - b[column].updatedAtTimestamp;
						case 'period':
							return a[column].startTimestamp - b[column].startTimestamp;
						case 'date':
							return a[column].sortableValue - b[column].sortableValue;
						case 'week':
							return a[column].sortableValue - b[column].sortableValue;
						case 'month':
							return a[column].sortableValue - b[column].sortableValue;
						case 'status':
							return a[column].state.localeCompare(b[column].state);
						case 'formats':
							if (!a[column].length && !b[column].length) {
								return 0;
							} else if (!a[column].length) {
								return -1;
							} else if (!b[column].length) {
								return 1;
							}

							return a[column][0].localeCompare(b[column][0]);
						default:
							return a[column][column].localeCompare(b[column][column]);
					}
				}

				return a[column] - b[column];
			}
		});

		return order === 'sorted-up' ? data : data.reverse();
	}

	/**
	 * Renderuje:
	 * - tlačítko pro přidání webu
	 * - select pro provedení hromadné akce s weby
	 * - select pro filtrování
	 */
	renderHeader() {
		const buttonsWrapper =
			this.section.querySelector(':scope > .Page-section-heading') ||
			Tools.createElement('div', { class: 'Page-section-heading' });

		if (window.SSP.data.urls.create) {
			const buttonAddRow = Tools.createElement('a', {
				class: 'Table-submitButton',
				href: window.SSP.data.urls.create,
				innerText: window.SSP.translations.table.buttons.new,
			});

			buttonsWrapper.appendChild(buttonAddRow);
		}

		this.selectCommonAction = new Select();

		this.selectCommonAction.initSelectWithParams(
			window.SSP.translations.table.comboboxActions,
			this.onSelectCommmonAction.bind(this),
			'selected',
			'Select--bigger Table-actions',
		);

		this._selectedDimensionFilter = window.SSP.data.dimensionFilter;
		this.selectFilterByDimension = new Select();

		this.selectFilterByDimension.initSelectWithParams(
			window.SSP.translations.table.dimensionSelector,
			this.onSelectFilterByDimension.bind(this),
			window.SSP.data.dimensionFilter,
			'Select--bigger Table-filter',
		);

		Tools.appendChilds(buttonsWrapper, this.selectFilterByDimension.getElement());

		Tools.appendChilds(buttonsWrapper, this.selectCommonAction.getElement());

		Tools.appendChilds(this.section, buttonsWrapper);
	}

	/**
	 * Nastaví jednu položku hlavičky tabulky.
	 * Přidá tooltip/atributy a pokud jí lze řadit, tak i listener.
	 * @param {object} data
	 * @param {Element} element
	 */
	setTableHeaderItem(data, element) {
		if (data.sortable && data.field !== this.pageIdName) {
			element.classList.add('is-sortable');
			element.addEventListener('click', this.sortTable.bind(this));
		} else if (element.classList.contains('Table-col--actions')) {
			element.addEventListener('change', this.onChangeRowsSelection.bind(this));
		}

		if (data.tooltip) {
			const tooltip = Tools.createTooltip(data.tooltip);

			tooltip.classList.add('Tooltip--table', 'Tooltip--th');
			element.appendChild(tooltip);
		}
	}

	/**
	 * Renderuje hlavičku tabulky:
	 * Weby Stav Druh webu..
	 */
	renderTableHeader() {
		this.thead = Tools.createElement('thead');

		const tr = Tools.createElement('tr');

		this.data.header.forEach((head) => {
			const { field, title } = head;

			if (excludedTableColumns.includes(field)) {
				return;
			}

			let classes = '';
			let title_ = '';

			switch (field) {
				case 'webId':
					classes = 'Table-col--actions';
					title_ = '<label><input type="checkbox"><span></span></label>';

					break;
				default:
					classes = 'Table-col--' + field;
					title_ = title || '';

					break;
			}

			const thContent = Tools.createElement('span', {
				class: 'Table-th-cont',
				innerHTML: title_,
				type: field,
			});

			const th = Tools.createElement('th', {
				class: 'Table-col ' + classes,
				innerHTML: '',
				type: field,
			});

			Tools.createElement('span');

			th.appendChild(thContent);
			tr.appendChild(th);
			this.setTableHeaderItem(head, th);
		});

		this.thead.appendChild(tr);
	}

	/**
	 * Vybere všechny weby/zony.
	 * @param {Event} e
	 */
	onChangeRowsSelection(e) {
		const { checked } = e.target;
		// označí/odznačí všechny vyrenderované weby/zony
		const inputs = Array.from(document.querySelectorAll('.Table-col input'));

		inputs.forEach((i) => {
			i.checked = checked;
		});

		// označí i všechny data, aby se zaškrtnutí uchovávalo při stránkování/řazení
		this.data.rows.forEach((row) => {
			row.checked = checked;
		});
	}

	/**
	 * Uživatel vybral web/zonu pomocí checkboxu.
	 * @param {Event} e
	 */
	onChangeRowSelection(e) {
		const { checked } = e.target;

		// není potřeba procházet celé rows, ale pouze renderovanou část
		for (let i = this.config.pageFirstIndex; i < this.config.pageLastIndex; i++) {
			if (Number(this.data.rows[i][this.pageIdName]) === Number(e.target.value)) {
				this.data.rows[i].checked = checked;

				break;
			}
		}
	}

	getChildOpener(row) {
		const openerSpan = Tools.createElement('span', {
			class: 'Table-child-opener',
		});

		openerSpan.setAttribute('data-' + this.pageIdName, row[this.pageIdName]);

		if (!row.name.urlPlus) {
			openerSpan.classList.add('is-disabled');
		} else {
			openerSpan.addEventListener('click', this.onClickExpandAndCollapseRows.bind(this));
		}

		return openerSpan;
	}

	/**
	 * Vygeneruje input a child opener v řádku tabulky.
	 * @param {object} row Data pro řádek tabulky.
	 * @return {Element} Vrací element s nastavenými atributy.
	 */
	getRowInputAndOpener(row) {
		const inputWrapper = Tools.createElement('td', {
			class: 'Table-col Table-col--actions',
		});

		const checkboxContainer = Tools.createElement('label', {
			class: 'Table-check',
		});

		const input = Tools.createElement('input', {
			// We want to prevent the browser from remembering the state of the checkbox when returning to the page using browser back button. Therefore, we set autocomplete to off.
			autocomplete: 'off',
			checked: !!row.checked,
			type: 'checkbox',
			value: row[this.pageIdName],
		});

		const inputFace = Tools.createElement('span');

		input.addEventListener('change', this.onChangeRowSelection.bind(this));

		Tools.appendChilds(checkboxContainer, input, inputFace);

		Tools.appendChilds(inputWrapper, checkboxContainer, this.getChildOpener(row));

		return inputWrapper;
	}

	/**
	 * Vygeneruje spany se statusem webu/zony/reportu...
	 * @param {object} rowData Data pro řádek tabulky.
	 * @return {Element} Vrací element s nastavenými atributy.
	 */
	getRowStatus(rowData) {
		const status = Tools.createElement('td', {
			class: 'Table-col Table-col--status',
		});

		if (this.complexTable) {
			return this.getComplexRowStatus(status, rowData);
		} else {
			return this.getSimpleRowStatus(status, rowData);
		}
	}

	/**
	 * Komplexní status, který umožňuje např. stopnutí/aktivaci webů.
	 * @param {Element} status
	 * @param {Object} rowData
	 */
	getComplexRowStatus(status, rowData) {
		const statusClass = 'Status Status--' + rowData.status.state;

		const statusText = window.SSP.translations.table.labels[rowData.status.state];
		const state = Tools.createElement('span', {
			class: statusClass,
			innerText: statusText,
		});

		state.setAttribute('data-' + this.pageIdName, rowData[this.pageIdName]);
		status.appendChild(state);

		status.addEventListener('click', (...params) => this.onClickStartStop(rowData.name.name, ...params));

		if (rowData.status.message) {
			const message = Tools.createElement('span', {
				class: 'Status Status--message',
				innerText: rowData.status.message,
			});

			status.appendChild(message);
		}

		return status;
	}

	/**
	 * Jednoduché zobrazení statusu reportu.
	 * @param {Element} status
	 * @param {Object} rowData
	 */
	getSimpleRowStatus(status, rowData) {
		const state = Tools.createElement('span', {
			class: 'Status Status--' + rowData.status.status,
			innerText: window.SSP.translations.table.statuses[rowData.status.status],
		});

		status.appendChild(state);

		if (rowData.status.hidden) {
			const hidden = Tools.createElement('span', {
				class: 'Status Status--hidden',
				innerText: window.SSP.translations.table.labels.hidden,
			});

			status.appendChild(hidden);
		}

		return status;
	}

	/**
	 * Změní stav webu/zony s daným id a překreslí.
	 * @param {string} state
	 * @param {number} id
	 */
	redrawRowState(state, id) {
		this.data.rows.forEach((row) => {
			if (Number(row[this.pageIdName]) === id) {
				row.status.state = state;
			}
		});

		this.redrawBody();
	}

	/**
	 * Při potvrzení akce bude web/zona aktivován/pozastaven.
	 * @param {string} url
	 * @param {string} state
	 * @param {string} id
	 */
	getActivationConfirmFunction(url, state, id) {
		return () => {
			api
				.POST(url)
				.then(() => {
					this.redrawRowState(state, Number(id));
				})
				.catch((error) => {
					console.error(error);
				});
		};
	}

	/**
	 * Pozastaví nebo aktivuje daný web. Tato akce musí být potvrzena akcí v modálním okně.
	 * @param {Event} e
	 */
	onClickStartStop(webName, e) {
		const id = e.target.getAttribute('data-' + this.pageIdName);

		if (!id) {
			return;
		}

		const state = e.target.classList.contains('Status--active') ? 'active' : 'inactive';

		const isFuture = e.target.classList.contains('Status--future');

		const url = (state === 'active' ? this.url.stop : this.url.start) + '?' + this.pageIdName + '=' + id;

		const { msg, activate, deactivate, prepareActivate, yes, no, noteActivate, noteDeactivate } =
			window.SSP.translations.table.confirmModal;

		let modalMsg = `${msg} `;
		let newState = null;

		if (state === 'inactive' && isFuture) {
			modalMsg += `${prepareActivate}?`;
			newState = 'active';
		}

		if (state === 'inactive' && !isFuture) {
			modalMsg += `${activate}?`;
			newState = 'active';
		}

		if (state === 'active') {
			modalMsg += `${deactivate}?`;
			newState = 'inactive';
		}

		const confirmCallback = () => this.getActivationConfirmFunction(url, newState, id)();

		let note = newState === 'active' ? noteActivate : noteDeactivate;

		note = note || '';
		note = note.replace('[[web_name]]', webName);

		// eslint-disable-next-line no-new
		new ConfirmDialog(modalMsg, note, yes, no, confirmCallback);
	}

	/**
	 * Adds labels to row name.
	 * @param {Array} labels
	 * @param {Element} parent
	 */
	renderLabel(labels = [], parent) {
		labels.forEach((labelData) => {
			const className = `Table-row-label${labelData.active ? '' : ' Table-row-label--inactive'}`;
			const label = Tools.createElement('a', {
				class: className,
				href: labelData.url,
				innerText: labelData.title,
			});

			parent.appendChild(label);
		});
	}

	/**
	 * Vytvoří buňku, která obsahuje jméno webu/zony/přehledu a ovládací prvky.
	 * @param {object} rowData Data pro řádek tabulky.
	 * @return {Element} Vrací element s nastavenými atributy.
	 */
	getRowNameAndActions(rowData) {
		const nameAndActions = Tools.createElement('td', {
			class: 'Table-col Table-col--name',
		});

		const wrapper = Tools.createElement('div', { class: 'Table-item' });

		nameAndActions.appendChild(wrapper);

		const nameValue = get(rowData, 'name.name') || get(rowData, 'web.name') || get(rowData, 'web');

		const title = Tools.createElement('div', {
			class: 'Table-item-title',
			innerText: Tools.formatValue(nameValue),
			title: Tools.formatValue(nameValue),
		});

		const actions = Tools.createElement('div', { class: 'Table-item-actions' });

		Tools.appendChilds(wrapper, title, actions);

		// Lze se prokliknout do detailu? Ano => přidání odkazu.
		if (has(rowData, 'name.urlDetails')) {
			title.innerText = '';

			title.appendChild(
				Tools.createElement('a', {
					class: 'is-clickable',
					href: rowData.name.urlDetails,
					innerText: Tools.formatValue(nameValue),
				}),
			);
		}

		if (this.pageName === 'web_services') {
			const moreAction = Tools.createElement('div', {
				class: 'Table-item-action Table-item-action--more',
			});

			actions.appendChild(moreAction);

			moreAction.addEventListener('click', () => {
				if (moreAction.hasChildNodes()) {
					return;
				}

				const popUpActions = Tools.createElement('div', {
					class: 'Table-item-pop-up-actions',
				});

				moreAction.appendChild(popUpActions);

				popUpActions.appendChild(
					Tools.createElement('a', {
						class: 'Table-item-pop-up-action Table-item-pop-up-action--edit',
						href: rowData.name.urlEdit,
						innerText: window.SSP.translations.table.labels.edit,
					}),
				);

				const handleDocumentClick = (event) => {
					const { target } = event;

					if (target === moreAction || target === popUpActions) {
						return;
					}

					moreAction.removeChild(popUpActions);
					document.removeEventListener('click', handleDocumentClick);
				};

				document.addEventListener('click', handleDocumentClick);
			});
		} else {
			this.getRowAction(actions, get(rowData, 'name.urlEdit'), 'edit');
		}

		if (has(rowData, 'name.urlCopy')) {
			if (this.pageName === 'reports') {
				this.createCopyAction(rowData.name.urlCopy, actions, rowData.id);
			} else {
				this.getRowAction(actions, rowData.name.urlCopy, 'copy');
			}
		}

		// AJAX smazání reportu.
		if (has(rowData, 'name.urlDelete')) {
			this.createDeleteAction(actions, rowData.id || rowData.codeId, nameValue, rowData.name.urlDelete);
		}

		this.renderLabel(rowData.labels, actions);

		return nameAndActions;
	}

	/**
	 * Vytvoří novou ikonu pro kopírování reportů a přidá akci.
	 * Při kopírování je odeslán na API request s příslušným ID.
	 * API vrátí kopírovaný řádek a ten je přidán do tabulky.
	 * @param {string} url
	 * @param {Element} elem
	 * @param {number} id
	 */
	createCopyAction(url, elem, id) {
		const action = Tools.createElement('div', {
			class: 'Table-item-action Table-item-action--copy',
			title: window.SSP.translations.table.labels.copy,
		});

		elem.appendChild(action);

		action.addEventListener('click', () => {
			api
				.POST(url)
				.then(({ data }) => {
					// Při renderování řádku budu mít třídu 'is-copied',
					// která vyvolá animaci.
					data.report.copied = true;

					// Vložím nový report za kopírovaný.
					const i = this.data.rows.map((row) => row.id).indexOf(id);

					this.data.rows.splice(i + 1, 0, data.report);
					this.initTableSize();
					this.updatePaginationArrows();
					this.redrawBody();

					// Kopírovaný report bude znova zpracován, takže čekám na změnu stavu.
					// Pokud jsou nějaké nezpracované reporty, tak už refresher běží a nevytvářím další.
					if (this.processedReportIds.length) {
						this.processedReportIds.push(data.report.id);
					} else {
						this.processedReportIds.push(data.report.id);
						this.reportRefresher();
					}
				})
				.catch((error) => {
					console.error(error);
				});
		});
	}

	/**
	 * Vytvoří tlačítko na odstranění položky z tabulky.
	 * @param {Element} elem Wrapper akcí.
	 * @param {number} id
	 * @param {string} webName
	 * @param {string} url
	 */
	createDeleteAction(elem, id, webName, url) {
		const action = Tools.createElement('div', {
			class: 'Table-item-action Table-item-action--delete',
			title: window.SSP.translations.table.labels.delete,
		});

		elem.appendChild(action);

		action.addEventListener('click', (e) => {
			const { msg, remove, yes, no } = window.SSP.translations.table.confirmModal;

			const note = window.SSP.translations.table.confirmModal.noteRemove
				? window.SSP.translations.table.confirmModal.noteRemove.replace('[[web_name]]', webName)
				: null;

			const msgHeader = `${msg} ${remove}?`;
			const confirmCallback = () => this.onClickDelete(e, id, url);

			// eslint-disable-next-line no-new
			new ConfirmDialog(msgHeader, note, yes, no, confirmCallback);
		});
	}

	/**
	 * Odstraní řádek
	 * @param {Event} e
	 * @param {number} id
	 * @param {string} url
	 */
	onClickDelete(e, id, url) {
		api
			.POST(url)
			.then(() => {
				// Nalezení řádku, který bude smazán.
				const removeIndex = this.data.rows.map((row) => row.id || row.codeId).indexOf(id);

				// Smazání a překreslení.
				if (~removeIndex) {
					this.data.rows.splice(removeIndex, 1);
				}

				const row = Tools.getParentByClass(e.target, 'Table-row');

				this.deleteRowAnimation(row);

				// Pokud mazaný řádek není hotový, tak ho musím odstranit ze seznamu
				// kontrolovaných reportů.
				if (this.processedReportIds.length) {
					const i = this.processedReportIds.indexOf(id);

					if (~i) {
						this.processedReportIds.splice(i, 1);
					}
				}
			})
			.catch((error) => {
				console.error(error);
			});
	}

	/**
	 * Odstraní řádky z tabulky s animací.
	 * @param {...Element} elems Elementy řádků k animaci a odstranění
	 */
	deleteRowAnimation(...elems) {
		elems.forEach((elem) => {
			elem.classList.remove('is-copied');
			elem.classList.add('is-deleted');
		});

		// Přidání třídy 'is-deleted' vyvolá animaci.
		// Čekám, než doběhne a pak překreslím tabulku.
		setTimeout(() => {
			this.initTableSize();
			this.updatePaginationArrows();
			this.redrawBody();
		}, 1000);
	}

	/**
	 * Vytvoří akci, která se zobrazí na hover.
	 * @param {Element} elem
	 * @param {string} url
	 * @param {string} name
	 */
	getRowAction(elem, url, name) {
		if (url) {
			const action = Tools.createElement('a', {
				class: 'Table-item-action Table-item-action--' + name,
				href: url,
				title: window.SSP.translations.table.labels[name],
			});

			elem.appendChild(action);
		}
	}

	/**
	 * Vytvoří element s formáty pro zónu.
	 * @param {object} rowData
	 */
	getRowFormats(rowData) {
		const cell = Tools.createElement('td', {
			class: 'Table-col',
		});

		// 200x200, 300x250
		if (this.complexTable) {
			cell.classList.add('Table-col--formats');
			cell.innerText = rowData.formats[0] || '';

			// Pokud je více formátu, tak se zobrazí pouze první a všechny
			// budou k náhledu uvnitř tooltipu.
			if (rowData.formats.length > 1) {
				const tooltip = Tools.createTooltip(
					rowData.formats.join(', '),
					window.SSP.translations.table.labels.usedFormats,
				);

				tooltip.classList.add('Tooltip--table', 'Tooltip--format');
				cell.appendChild(tooltip);
			}
		} else {
			// CSV, TSV (pro Excel)
			cell.classList.add('Table-col--formatsForDownload');

			rowData.formats.forEach((format) => {
				const href = Tools.createElement('a', {
					href: format.urlDownload,
					innerText: window.SSP.translations.table.formats[format.type],
				});

				cell.appendChild(href);
			});
		}

		return cell;
	}

	/**
	 * Vytvoří buňku s periodou (od - do).
	 * @param {object} rowData
	 */
	getRowPeriod(rowData) {
		return Tools.createElement('td', {
			class: 'Table-col Table-col--period',
			innerText: rowData.period.start + ' - ' + rowData.period.finish,
		});
	}

	openWebInterfacesDialog(webId, webIntefaces) {
		// eslint-disable-next-line no-new
		new WebInterfacesDialog('Rozhraní', webId, webIntefaces);
	}

	getRowWebInterfaces(rowData) {
		// we want to render XHR and VAST interfaces on one line and
		// OpenRTB interface on next line separately
		const xhrVastInterfaces = rowData.webInterfaces.filter(
			(webInterface) => webInterface === WebInterfacesType.VAST || webInterface === WebInterfacesType.XHR,
		);

		const rtbInterface = rowData.webInterfaces.find((webInterface) => webInterface === WebInterfacesType.OpenRTB);

		const textContainers = [];

		if (xhrVastInterfaces.length > 0) {
			const xhrVastDiv = Tools.createElement('div', {
				innerText: `${xhrVastInterfaces.join(', ')}`,
			});

			// if OpenRTB interface is active we want to render comma because
			// there will be OpenRTB on the next line
			if (rtbInterface) {
				xhrVastDiv.innerText += ',';
			}

			textContainers.push(xhrVastDiv);
		}

		if (rtbInterface) {
			const rtbDiv = Tools.createElement('div', {
				innerText: rtbInterface,
			});

			textContainers.push(rtbDiv);
		}

		const webInterfacesCell = Tools.createElement('td', {
			class: 'Table-col Table-col--webInterfaces',
		});

		if (window.SSP.data.rights.allow_web_columns_interface_edit) {
			webInterfacesCell.addEventListener('click', () =>
				this.openWebInterfacesDialog(rowData.webId, rowData.webInterfaces),
			);

			webInterfacesCell.classList.add('is-clickable');
		}

		Tools.appendChilds(webInterfacesCell, ...textContainers);

		return webInterfacesCell;
	}

	/**
	 * Vytvoří buňku s datem
	 * @param {object} dateString string with the date
	 * TODO prejmenovat, ale getRowDate uz existuje
	 */
	getRowDate(dateString) {
		return Tools.createElement('td', {
			class: 'Table-col Table-col--datestring',
			innerText: dateString,
		});
	}

	/**
	 * Vytvoří buňku s akcemi.
	 * @param {object} rowData
	 */
	getRowActions(rowData) {
		if (rowData.actions.urlRun) {
			const link = Tools.createElement('a', {
				href: rowData.actions.urlRun,
				innerText: window.SSP.translations.table.labels.run,
			});

			const cell = Tools.createElement('td', {
				class: 'Table-col Table-col--templateActions',
			});

			cell.appendChild(link);

			return cell;
		} else {
			return Tools.createElement('td', {
				class: 'Table-col',
				innerHTML: '',
			});
		}
	}

	/**
	 * Vytvoří buňku s částí data.
	 * @param {object} rowData
	 * @param {string} rowData
	 */
	getRowDatePart(rowData, part = 'date') {
		return Tools.createElement('td', {
			class: `Table-col Table-col--${part}`,
			innerText: rowData[part].value,
		});
	}

	getRowWebName(rowData) {
		return Tools.createElement('td', {
			class: 'Table-col Table-col--webname',
			innerText: rowData.web.name,
		});
	}

	/**
	 * Compose common cell with a link
	 * @param {string} text Link anchor text
	 * @param {string} url Link url
	 * @param {string} [title=null] title attribute
	 * @param {string} [target=null] target attribute
	 */
	getRowLink(text, url, title = null, target = null) {
		const link = Tools.createElement('a', {
			href: url,
			innerText: text,
		});

		if (title) {
			link.title = title;
		}

		if (target) {
			link.target = target;
		}

		const cell = Tools.createElement('td', {
			class: 'Table-col Table-col--link',
		});

		cell.appendChild(link);

		return cell;
	}

	getRowWebIdLink(row) {
		return this.getRowLink(
			row.webIdLink,
			`/webs/${row.webIdLink}`,
			`${SSP.translations.table.labels.goToWeb} ${row.name.name}`,
		);
	}

	/**
	 * @param {{logoUrl: string | null}} row
	 * @return {HTMLTableDataCellElement}
	 */
	getRowLogo(row) {
		const cell = document.createElement('td');

		cell.className = 'Table-col Table-col--image';

		const { logoUrl } = row;

		if (logoUrl) {
			const image = document.createElement('img');

			image.alt = 'Logo';
			image.src = logoUrl;

			cell.appendChild(image);
		} else {
			cell.textContent = Tools.formatValue(logoUrl);
		}

		return cell;
	}

	/**
	 * Renderuje řádky tabulky.
	 */
	renderTableBody() {
		this.tbody = Tools.createElement('tbody');

		// kvůli stránkování renderuje pouze vybraný rozsah
		for (let i = this.config.pageFirstIndex; i < this.config.pageLastIndex; i++) {
			const row = this.data.rows[i];

			const tr = Tools.createElement('tr', {
				class: 'Table-row' + (row.copied ? ' is-copied' : ''),
			});

			tr.setAttribute('data-id', row.id || row.webId || row.codeId);
			// Nechceme znova animaci při stránkování.
			row.copied = false;

			// renderuje pouze property z hlavičky
			this.data.header.forEach((head) => {
				// pokud property existuje v rows
				if (head.field in row && !excludedTableColumns.includes(head.field)) {
					let cell = null;

					switch (head.field) {
						case 'webId':
							cell = this.getRowInputAndOpener(row);

							break;
						case 'name':
							cell = this.getRowNameAndActions(row);

							break;
						case 'status':
							cell = this.getRowStatus(row);

							break;
						case 'formats':
							cell = this.getRowFormats(row);

							break;
						case 'period':
							cell = this.getRowPeriod(row);

							break;
						case 'webInterfaces':
							cell = this.getRowWebInterfaces(row);

							break;
						case 'created':
						case 'createdAt':
						case 'updatedAt':
							cell = this.getRowDate(row[head.field][head.field]);

							break;
						case 'actions':
							cell = this.getRowActions(row);

							break;
						case 'date':
						case 'week':
						case 'month':
							cell = this.getRowDatePart(row, head.field);

							break;
						case 'web':
							cell = this.getRowNameAndActions(row);

							break;
						case 'webIdLink':
							cell = this.getRowWebIdLink(row);

							break;
						case 'logoUrl':
							cell = this.getRowLogo(row);

							break;
						default:
							cell = Tools.createElement('td', {
								class: 'Table-col Table-col--' + head.field,
								innerText: Tools.formatValue(row[head.field], head.type),
							});

							break;
					}

					tr.appendChild(cell);
				}
			});

			Tools.appendChilds(this.tbody, tr);
		}
	}

	/**
	 * Renderuje patičku tabulky:
	 * Celkem 322 323 3 233 223
	 */
	renderTableFooter() {
		this.tfoot = Tools.createElement('tfoot');

		const tr = Tools.createElement('tr');

		// renderuje pouze property z hlavičky
		this.data.header.forEach((head, index) => {
			if (excludedTableColumns.includes(head.field)) {
				return;
			}

			let cell = null;

			if (head.field in this.data.footer) {
				cell = Tools.createElement('td', {
					class: 'Table-col Table-col--' + head.field,
					innerText: Tools.formatValue(this.data.footer[head.field], head.type),
				});
				// pokud má footer title (není v headeru) a je to první sloupec tabulky
			} else if ('title' in this.data.footer && !index) {
				cell = Tools.createElement('td', {
					class: 'Table-col Table-col--total',
					innerText: this.data.footer.title,
				});
				// prázdná buňka aby se zahovaly proporce tabulky
			} else {
				cell = Tools.createElement('td', { class: 'Table-col' });
			}

			tr.appendChild(cell);
		});

		Tools.appendChilds(this.tfoot, tr);
	}

	/**
	 * Nastaví velikost (meze) renderované tabulky
	 * podle nastavení z local storage.
	 */
	initTableSize() {
		if (
			!this._isTableFullyUsed(
				this.config.pageSize,
				this.config.pageFirstIndex,
				this.config.pageLastIndex,
				this.data.rows.length,
			)
		) {
			this.config.pageLastIndex = this.config.pageFirstIndex + this.config.pageSize;
		}

		// Pro případ, že jsem na Nté stránce tabulky a přepnu se na jiného uživatele,
		// který nemusí mít tolik stránek v tabulce => musím resetovat nastavení.
		if (
			this.data.rows.length <= this.config.pageFirstIndex ||
			(this.config.pageSize - this.config.pageLastIndex > 0 && this.config.pageLastIndex < this.data.rows.length)
		) {
			this.config.pageFirstIndex = 0;
			this.config.pageLastIndex = this.config.pageSize;
			Tools.updateLocalStorage(this.storageItemName, this.config);
		}

		if (this.config.pageLastIndex > this.data.rows.length) {
			this.config.pageLastIndex = this.data.rows.length;
		}
	}

	/**
	 * Konroluje zda je zobrazen maximální počet řádků.
	 * Kvůli kopírování řádků.
	 * @param {number} pageSize
	 * @param {number} start
	 * @param {number} end
	 * @param {number} dateLength
	 */
	_isTableFullyUsed(pageSize, start, end, dateLength) {
		if (end - start < pageSize && end < dateLength) {
			return false;
		} else {
			return true;
		}
	}

	/**
	 * Nastaví tabulku na výchozí hodnoty = posune na začátek.
	 */
	resetTableSize() {
		this.config.pageFirstIndex = 0;

		this.config.pageLastIndex = this.config.pageFirstIndex + this.config.pageSize;

		if (this.config.pageLastIndex > this.data.rows.length) {
			this.config.pageLastIndex = this.data.rows.length;
		}

		Tools.updateLocalStorage(this.storageItemName, this.config);
	}

	/**
	 * Renderuje:
	 * - Header včetně Tooltips
	 * - Body
	 * - Footer se summary
	 */
	renderTable() {
		this.tableMainWrapper = Tools.createElement('div', {
			class: 'TableMainWrapper',
		});

		this.tableWrapper = Tools.createElement('div', { class: 'TableWrapper' });

		this.table = Tools.createElement('table', {
			class: 'Table Table--primary Table--' + this.pageName,
		});

		this.renderTableHeader();

		if (this.config.sortedColumn) {
			this.sortTable(this.config.sortedColumn.columnType, this.config.sortedColumn.sorted);
		}

		this.renderTableBody();

		if (this.complexTable) {
			this.renderTableFooter();
		}

		// Kompletace tabulky a vložení do DOMu.
		Tools.appendChilds(this.table, this.thead, this.tbody, this.tfoot);
		Tools.appendChilds(this.tableWrapper, this.table);
		Tools.appendChilds(this.tableMainWrapper, this.tableWrapper);
		Tools.appendChilds(this.section, this.tableMainWrapper);
	}

	/**
	 * Inicializuje tlačítka navigace <, >.
	 */
	updatePaginationArrows() {
		this.nextPageButton.classList.remove('is-active');
		this.prevPageButton.classList.remove('is-active');

		if (this.config.pageLastIndex < this.data.rows.length) {
			this.nextPageButton.classList.add('is-active');
		}

		if (this.config.pageFirstIndex > 0) {
			this.prevPageButton.classList.add('is-active');
		}
	}

	/**
	 * Vyrenderuje nově zadaný počet webů/zon.
	 */
	renderNewNumberOfSites() {
		this.resetTableSize();
		this.updatePaginationArrows();
		this.redrawBody();
	}

	/**
	 * Reaguje na změnu počtu zobrazovaných webů.
	 * @param {string} selected
	 */
	onSelectNumberOfRows(selected) {
		this.config.pageSize = Number(selected);
		this.renderNewNumberOfSites();
		this.selectNumberOfRows.setSelectOption(selected);
		this.selectNumberOfRows.toggleSelect();
	}

	/**
	 * Reaguje na filtrování webů podle jejich aktivity.
	 * @param {string} selected
	 */
	onSelectActivityFilter(selected) {
		// Všechny weby jsou načteny zvona bez ohledu na předchozí výběr.
		this.customSelection = null;

		// Notifikace grafu o změně.
		const event = new CustomEvent('rows-activity-changed', {
			detail: { selected },
		});

		window.dispatchEvent(event);

		api
			.GET(this.url.tableData + getDateForUrl() + this.getActivityForUrl(selected))
			.then(({ data }) => {
				this.data = data;

				if (this.config.sortedColumn) {
					this.sortTable(this.config.sortedColumn.columnType, this.config.sortedColumn.sorted);
				}

				this.resetTableSize();
				this.redrawHeader();
				this.redrawBody();
				this.redrawFooter();
				this.updatePaginationArrows();
			})
			.catch((error) => {
				console.error(error);
			});

		this.selectActivityFilter.setSelectOption(selected);
		this.selectActivityFilter.toggleSelect();
	}

	/**
	 * Změní stránku tabulky.
	 * @param {Event} e
	 */
	onClickChangeTablePage(e) {
		// Pokud je tlačítko aktivní
		if (e.target.classList.contains('is-active')) {
			// Další strana
			if (e.target.classList.contains('Pagination-nextButton')) {
				this.config.pageFirstIndex += this.config.pageSize;

				this.config.pageLastIndex = this.config.pageFirstIndex + this.config.pageSize;

				if (this.config.pageLastIndex >= this.data.rows.length) {
					this.config.pageLastIndex = this.data.rows.length;
				}
			} else {
				// předcházející strana
				this.config.pageFirstIndex -= this.config.pageSize;

				this.config.pageLastIndex = this.config.pageFirstIndex + this.config.pageSize;

				if (this.config.pageFirstIndex <= 0) {
					this.config.pageFirstIndex = 0;
				}
			}

			this.updatePaginationArrows();
			Tools.updateLocalStorage(this.storageItemName, this.config);
			this.redrawBody();
		}
	}

	/**
	 * Renderuje:
	 * - select pro výběr počtu řádků
	 * - select pro zobrazení aktivních webů/zon
	 * - šipky pro stránkování
	 */
	renderPagination() {
		const paginationWrapper = Tools.createElement('div', {
			class: 'Pagination',
		});

		const paginationArrowsWrapper = Tools.createElement('div', {
			class: 'Pagination-arrows',
		});

		// <<<
		this.prevPageButton = Tools.createElement('button', {
			class: 'Pagination-prevButton',
		});

		this.prevPageButton.addEventListener('click', this.onClickChangeTablePage.bind(this));

		// >>>
		this.nextPageButton = Tools.createElement('button', {
			class: 'Pagination-nextButton',
		});

		this.nextPageButton.addEventListener('click', this.onClickChangeTablePage.bind(this));

		Tools.appendChilds(paginationArrowsWrapper, this.prevPageButton, this.nextPageButton);

		// 5 řádků, 10 řádků...
		this.selectNumberOfRows = new Select();

		this.selectNumberOfRows.initSelectWithParams(
			window.SSP.translations.table.pageSelector,
			this.onSelectNumberOfRows.bind(this),
			this.config.pageSize,
			'Select--bigger Pagination-selectNumber',
		);

		if (this.complexTable) {
			this.selectActivityFilter = new Select();

			this.selectActivityFilter.initSelectWithParams(
				window.SSP.translations.table.webActivityFilter,
				this.onSelectActivityFilter.bind(this),
				window.SSP.data.webActivityFilter,
				'Select--bigger Pagination-selectFilter',
			);

			Tools.appendChilds(
				paginationWrapper,
				this.selectNumberOfRows.getElement(),
				this.selectActivityFilter.getElement(),
				paginationArrowsWrapper,
			);
		} else {
			Tools.appendChilds(paginationWrapper, this.selectNumberOfRows.getElement(), paginationArrowsWrapper);
		}

		Tools.appendChilds(this.section, paginationWrapper);
	}

	/**
	 * Fuknce pro rozpadnutí nebo zabalení webu/zony. Naslouchá na event
	 * @param {Event} e Objekt události
	 * @example
	 * button.addEventListener('click', this.onClickExpandAndCollapseRows.bind(this));
	 */
	onClickExpandAndCollapseRows(e) {
		if (e.target.getAttribute('data-pending')) {
			return;
		}

		const clickedRow = e.target.parentNode.parentNode;
		const id = Number(e.target.getAttribute('data-' + this.pageIdName));

		if (e.target.classList.contains('is-open')) {
			this.collapseRow(clickedRow, e.target);
		} else {
			e.target.setAttribute('data-pending', true);

			this.expandRow(e.target)
				.then(({ data }) => {
					if (data.rows.length) {
						e.target.classList.toggle('is-open');

						data.rows.forEach((rowData) => {
							this.renderExpandRow(rowData, id, clickedRow);
						});
					} else {
						e.target.classList.remove('is-open');
						this.removeExpandRow(id);
						renderNotification({ feel: 'info', title: window.SSP.translations.messages.noData });
					}

					e.target.removeAttribute('data-pending');
				})
				.catch((error) => {
					console.error(error);
				});
		}
	}

	/**
	 * Fuknce pro rozpadnutí webu.
	 * @param {Element} expandButton Objekt DOMu, který obsahuje button na rozpadnutí webu
	 * @example
	 * this.expandRow(e.target);
	 */
	expandRow(expandButton) {
		const id = Number(expandButton.getAttribute('data-' + this.pageIdName));
		const url = this.getExpandRowUrl(id);

		//this.expandedRows = !this.expandedRows ? [id] : this.expandedRows.push(id);
		if (this.expandedRows) {
			this.expandedRows.push(id);
		} else {
			this.expandedRows = [id];
		}

		return api.GET(url);
	}

	removeExpandRow(id) {
		const position = this.expandedRows.indexOf(id);

		if (~position) {
			this.expandedRows.splice(position, 1);
		}
	}

	/**
	 * Fuknce pro zabalení webu/zony.
	 * @param {Element} row Objekt DOMu, který obsahuje <tr> tabulky
	 * @param {Element} expandButton Objekt DOMu, který obsahuje button na rozpadnutí webu/zony
	 * @example
	 * this.collapseRow(row, event.target);
	 */
	collapseRow(row, expandButton) {
		const id = Number(expandButton.getAttribute('data-' + this.pageIdName));

		this.removeExpandRow(id);
		expandButton.classList.toggle('is-open');

		while (row.nextSibling && row.nextSibling.hasAttribute('data-' + this.pageIdName)) {
			row.parentNode.removeChild(row.nextSibling);
		}
	}

	/**
	 * Fuknce pro vyrendrování <tr> prvků rozpadlého webu.
	 * @param {object} data data jednoho webu (row)
	 * @param {number} id id webu
	 * @param {Element} prevRow object DOMu <tr> webu
	 * @example
	 * this.renderExpandRow(row, id, prevRow);
	 */
	renderExpandRow(data, id, prevRow) {
		let cell = null;
		const expandedRow = Tools.createElement('tr', { class: 'Table-child' });

		expandedRow.setAttribute('data-' + this.pageIdName, id);

		this.data.header.forEach((head) => {
			if (excludedTableColumns.includes(head.field)) {
				return;
			}

			if (head.field in data) {
				cell = Tools.createElement('td', {
					class: 'Table-col Table-col--' + head.field,
					innerText: this.isImpCPTHidden(head.field) ? '-' : Tools.formatValue(data[head.field], head.type),
				});
			} else {
				cell = Tools.createElement('td', { class: 'Table-col' });
			}

			expandedRow.appendChild(cell);
		});

		prevRow.parentNode.insertBefore(expandedRow, prevRow.nextSibling);
	}

	/**
	 * Fuknce pro vygenerování url pro API
	 * @param {number} id id webu
	 * @example
	 * let url = this.getExpandRowUrl(id);
	 */
	getExpandRowUrl(id) {
		let url = '';
		let rowUrl = '';

		for (let i = 0; i < this.data.rows.length; i++) {
			// eslint-disable-next-line typescript-eslint/no-unnecessary-type-conversion
			if (Number(this.data.rows[i][this.pageIdName]) === Number(id)) {
				rowUrl = this.data.rows[i].name.urlPlus;

				break;
			}
		}

		url = rowUrl + '&' + getDateForUrl() + '&dimension=' + this.selectFilterByDimension.getSelectedOption();

		return url;
	}

	/**
	 * Pokud je tabulka větší, než její wrapper, tak se zobrazí její horizontální scroller.
	 */
	setTableScroller() {
		if (this.tableMainWrapper.clientWidth < this.table.clientWidth) {
			this.tableMainWrapper.classList.add('is-scrollable');
		} else {
			this.tableMainWrapper.classList.remove('is-scrollable');
		}
	}

	/**
	 * Reaguje na změnu velikosti okna/tabulky.
	 * Případně zobrazí scrollbar tabulky.
	 */
	listenOnTableSize() {
		this.setTableScroller();
		window.addEventListener('resize', this.setTableScroller.bind(this));
	}
}

export default Table;
